import { useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid';
import pendingRequestsStyle from '../../Styles/ComponentStyles/PendingRequests/PendingRequests.module.scss';
import { PendingRequestsTableTooltip } from './PendingRequestsTableTooltip';

interface OverflowTooltipCellProps {
    text: string;
}

function measureElementOverflow(el: HTMLElement): boolean {
    return el.scrollWidth > el.clientWidth + 1;
}

export function OverflowTooltipCell({ text }: OverflowTooltipCellProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLSpanElement>(null);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const display = (text ?? '').trim() || '—';

    useLayoutEffect(() => {
        const container = containerRef.current;
        const textEl = textRef.current;
        if (!container || !textEl) return;

        const measureOverflow = () => {
            const overflow =
                measureElementOverflow(textEl) || measureElementOverflow(container);
            setIsOverflowing(overflow);
        };

        measureOverflow();

        const rafId = requestAnimationFrame(measureOverflow);

        const resizeObserver = new ResizeObserver(measureOverflow);
        resizeObserver.observe(container);
        resizeObserver.observe(textEl);

        return () => {
            cancelAnimationFrame(rafId);
            resizeObserver.disconnect();
        };
    }, [display]);

    const content = (
        <Box ref={containerRef} className={pendingRequestsStyle.overflowTooltipCell}>
            <span ref={textRef} className={pendingRequestsStyle.overflowTooltipText} title="">
                {display}
            </span>
        </Box>
    );

    return (
        <PendingRequestsTableTooltip title={display} disable={!isOverflowing}>
            {content}
        </PendingRequestsTableTooltip>
    );
}

export function renderOverflowTextCell(params: GridRenderCellParams) {
    return <OverflowTooltipCell text={String(params.value ?? '')} />;
}
